import { useSyncExternalStore } from "react";

/**
 * Personal mute ("block"): hide a user's messages from ME only — private,
 * instant, per-device (localStorage). Keyed by lowercase username (chat
 * messages don't carry user ids; usernames are unique per room). This is
 * NOT a room mute — the blocked user stays fully present for everyone else.
 */
const KEY = "yay:blocked";
type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Cached snapshot for useSyncExternalStore — getSnapshot MUST return a stable
 * reference between changes, or React re-renders forever ("Maximum update
 * depth exceeded" = blank screen the moment the room mounts, because
 * ChatPanel calls useBlocked). Refreshed only when the store emits.
 */
let snapshot: string[] = load();

function emit() {
  snapshot = load();
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) emit();
  });
}

function load(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

export function getBlocked(): string[] {
  return snapshot;
}

export function isBlocked(username: string): boolean {
  const name = username.toLowerCase();
  return load().includes(name);
}

export function toggleBlocked(username: string): void {
  const name = username.toLowerCase();
  let list: string[];
  try {
    list = load();
  } catch {
    list = [];
  }
  list = list.includes(name)
    ? list.filter((u) => u !== name)
    : [...list, name];
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
  emit();
}

/** Reactive hook — re-renders on block/unblock (including other tabs). */
export function useBlocked(): Set<string> {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getBlocked,
  );
  return new Set(list);
}
